import { useQuery } from '@tanstack/react-query'
import { fetchDevices, getUser } from './_auth'
import { getAdminPortalUrl, getDrupalUrl, getDrupalUser } from './_api'
import { identify } from '../../utils/analytics'
import { CustomCognitoUser } from './_types'

export const useAuthDataState = () => {
  return useQuery({
    queryKey: ['auth'],
    queryFn: getUser,
    onSuccess: (data: CustomCognitoUser) => {
      identify(data.attributes?.sub, data.attributes)
    },
    refetchOnWindowFocus: false,
    staleTime: 30000,
    retry: false,
  })
}

export const useAuthDevicesState = () => {
  return useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices,
    refetchOnWindowFocus: true,
    staleTime: 30000,
    retry: false,
  })
}

export const useDrupalUserDataState = (userId?: string, enabled = true) => {
  return useQuery({
    queryKey: ['drupal-user', userId],
    queryFn: getDrupalUser,
    enabled: enabled && !!userId,
    refetchOnWindowFocus: false,
    staleTime: 30000,
    retry: false,
  })
}

export const useMasqueradeState = (userData?: CustomCognitoUser) => {
  const isMasquerading =
    !!userData?.signInUserSession?.idToken?.payload?.masquerading
  const { data: drupalUserData, isLoading: isDrupalUserLoading } =
    useDrupalUserDataState(
      userData?.attributes?.['custom:user_id'],
      isMasquerading
    )
  const memberName =
    [userData?.attributes?.given_name, userData?.attributes?.family_name]
      .filter(Boolean)
      .join(' ') || userData?.attributes?.email
  const switchBackUrl = drupalUserData?.userSwitch
    ? new URL(drupalUserData.userSwitch, getDrupalUrl()).toString()
    : null
  const endMasqueradeUrl = getAdminPortalUrl()
  const actionUrl =
    switchBackUrl ||
    (isMasquerading && !isDrupalUserLoading ? endMasqueradeUrl : null)
  const actionLabel = switchBackUrl ? 'Switch Back' : 'End Masquerade'
  const shouldClearAuthCookies = !switchBackUrl && !!actionUrl

  return {
    isMasquerading,
    memberName,
    actionLabel,
    actionUrl,
    endMasqueradeUrl,
    shouldClearAuthCookies,
    switchBackUrl,
  }
}
